import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTmdbSearch } from './useTmdbSearch'
import type { TmdbSearchResult } from '../tmdb/tmdb'
import { useInLibrary } from '../library/useInLibrary'
import { LazyImage } from '../ui'

interface Props {
  open: boolean
  onClose: () => void
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen: (personId: number) => void
}

const RECENT_KEY = 'govu.pwa.recentSearches.v1'
const RECENT_MAX = 8

function loadRecent(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string').slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

function pushRecent(query: string): string[] {
  const trimmed = query.trim()
  if (!trimmed || typeof window === 'undefined') return loadRecent()
  const current = loadRecent().filter(
    (q) => q.toLowerCase() !== trimmed.toLowerCase()
  )
  const next = [trimmed, ...current].slice(0, RECENT_MAX)
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    /* localStorage disabled — ignore */
  }
  return next
}

/**
 * Fullscreen search overlay — the PWA's limited search surface.
 *
 * This is deliberately a stripped-down version of the desktop search:
 * exact-title TMDB lookup only, no AI semantic search, no subtitle
 * integration, no library-relative ranking. The full experience (AI
 * search across descriptions / cast / vibe, subtitle downloads,
 * library-aware recommendations) lives in the desktop installer. A
 * prominent notice at the top of the results region tells the user
 * this and links to /download.
 *
 *   - Debounced TMDB /search/multi auto-search with cancellation
 *   - Manual search button + Enter key submit + Clear (×) button
 *   - Result grid with poster, title, year, TMDB rating, Movie/TV badge
 *   - "In your library" badge on results already in the library
 *   - Pagination via "Load more" button at bottom of the grid
 *   - Recent searches stored in localStorage, surfaced in the empty state
 *   - Full-screen on phones, modal on desktop (responsive container)
 */
export function SearchOverlay({ open, onClose, onOpenDetail, onPersonOpen }: Props) {
  const [query, setQuery] = useState('')
  const [excludeLibrary, setExcludeLibrary] = useState(false)
  const [recent, setRecent] = useState<string[]>(() => loadRecent())
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inLibrary = useInLibrary()

  // TMDB is the only search backend on the PWA. AI search used to be
  // toggleable here but the client-side Groq path diverged too far
  // from the desktop's main-process flow to be honestly called "the
  // same search", so it was removed in favour of a single consistent
  // TMDB path + a clear "download desktop for full search" notice.
  const tmdb = useTmdbSearch(query)

  // Focus the input and lock scroll when the overlay opens; reset all
  // state on close so the next open is a clean slate.
  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    const focusHandle = setTimeout(() => inputRef.current?.focus(), 40)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(focusHandle)
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      setRecent(loadRecent())
      return
    }
    // Reset when the overlay closes.
    setQuery('')
    setExcludeLibrary(false)
    tmdb.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handlePickTmdb = useCallback(
    (r: TmdbSearchResult) => {
      setRecent(pushRecent(query))
      if (r.kind === 'person') {
        onPersonOpen(r.tmdbId)
      } else {
        onOpenDetail(r.tmdbId, r.kind)
      }
      // Defer onClose to a microtask so React can commit the detail
      // modal's useBackButtonClose effect BEFORE this search overlay's
      // cleanup runs. Otherwise: the searchOpen hook effect fires
      // first in the same render, sees its entry is still on top of
      // the shared history stack, calls history.back() — the async
      // popstate then lands AFTER the detail modal pushed its own
      // entry, and handlePopState pops the detail modal's entry and
      // fires its onClose. From the user's POV clicking a result
      // appeared to do nothing because the detail modal was opened
      // and then immediately closed on the same tick. The microtask
      // delay lets the detail's entry become the new top, at which
      // point search's removeEntry sees wasOnTop=false and skips the
      // history.back() dance entirely.
      setTimeout(onClose, 0)
    },
    [onClose, onOpenDetail, onPersonOpen, query]
  )

  const handleClear = useCallback(() => {
    setQuery('')
    tmdb.reset()
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [tmdb])

  const handleSubmit = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    setRecent(pushRecent(trimmed))
    tmdb.runNow()
  }, [query, tmdb])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        if (query) handleClear()
        else onClose()
      }
    },
    [handleClear, handleSubmit, onClose, query]
  )

  const handleRecentClick = useCallback(
    (q: string) => {
      setQuery(q)
      setRecent(pushRecent(q))
    },
    []
  )

  // Filter out results that are in the user's library when the filter
  // toggle is on. The toggle itself is only surfaced once results are
  // in, to match desktop behaviour.
  const filteredTmdb = useMemo(() => {
    if (!excludeLibrary) return tmdb.results
    return tmdb.results.filter((r) => {
      if (r.kind === 'movie') return !inLibrary.movies.has(r.tmdbId)
      if (r.kind === 'tv') return !inLibrary.series.has(r.tmdbId)
      return true
    })
  }, [excludeLibrary, inLibrary, tmdb.results])

  const tmdbTitles = useMemo(
    () => filteredTmdb.filter((r) => r.kind !== 'person'),
    [filteredTmdb]
  )
  const tmdbPeople = useMemo(
    () => filteredTmdb.filter((r) => r.kind === 'person'),
    [filteredTmdb]
  )

  const libraryFilteredCount = tmdb.results.length - filteredTmdb.length

  const hasAnyLibraryMatch = useMemo(
    () =>
      tmdb.results.some((r) => {
        if (r.kind === 'movie') return inLibrary.movies.has(r.tmdbId)
        if (r.kind === 'tv') return inLibrary.series.has(r.tmdbId)
        return false
      }),
    [tmdb.results, inLibrary]
  )

  if (!open) return null

  const trimmedQuery = query.trim()
  const showEmptyState = !trimmedQuery

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col bg-bg-primary font-inter sm:items-start sm:justify-center sm:bg-black/60 sm:p-6"
      onMouseDown={(e) => {
        // Click-outside to close on desktop (modal mode).
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex h-full w-full flex-col sm:h-[min(90vh,900px)] sm:max-w-4xl sm:overflow-hidden sm:rounded-xl sm:border sm:border-border-primary sm:bg-bg-primary sm:shadow-2xl">
        {/* Header: search bar + controls */}
        <div className="border-b border-border-primary">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}
            className="flex items-center gap-2 px-4 py-3"
          >
            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon
                  className={`h-4 w-4 transition-colors ${
                    trimmedQuery ? 'text-accent-primary' : 'text-text-tertiary'
                  }`}
                />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search for movies and TV shows…"
                className={`w-full rounded-lg border-2 bg-bg-tertiary py-3 pl-10 pr-20 text-sm text-text-primary placeholder-text-tertiary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-primary/30 ${
                  trimmedQuery
                    ? 'border-accent-primary bg-bg-secondary shadow-lg shadow-accent-primary/10'
                    : 'border-border-primary hover:border-border-secondary hover:bg-bg-secondary'
                }`}
                role="searchbox"
                aria-label="Search TMDB for movies and TV shows"
                autoComplete="off"
                spellCheck={false}
              />
              {/* Inline loading spinner */}
              {tmdb.loading && (
                <div className="pointer-events-none absolute inset-y-0 right-12 flex items-center">
                  <Spinner />
                </div>
              )}
              {/* Clear button */}
              {query && !tmdb.loading && (
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label="Clear search"
                  className="absolute inset-y-0 right-12 flex items-center pr-1 text-text-tertiary transition-colors hover:text-text-secondary"
                >
                  <svg
                    className="h-4 w-4 transition-transform hover:rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {/* Manual submit button */}
              <button
                type="submit"
                aria-label="Search"
                className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-accent-primary transition-colors hover:bg-accent-primary/10"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-bg-secondary px-3 py-2 text-sm text-text-secondary hover:bg-bg-tertiary"
            >
              Close
            </button>
          </form>

          {/* Limited-search notice + library-filter row. The notice
              replaces the old TMDB / AI mode toggle — we used to let
              users pick between a browser-side Groq search and a TMDB
              lookup, but the Groq path drifted far enough from the
              desktop's main-process AI search that it was misleading
              to call them "the same." PWA is now exact-title TMDB
              only, and links the user to the desktop install for the
              full-fat experience. */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-3">
            <div className="flex items-start gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-[11px] text-text-secondary">
              <span className="mt-0.5">⚠️</span>
              <div className="leading-snug">
                Web search is limited — exact movie or series titles only. For AI search across descriptions, cast, vibe, and library-aware recommendations,{' '}
                <Link
                  to="/download"
                  className="font-semibold text-accent-primary underline-offset-2 hover:underline"
                  onClick={onClose}
                >
                  download the desktop app
                </Link>
                .
              </div>
            </div>
            {tmdb.results.length > 0 && hasAnyLibraryMatch && (
              <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  checked={excludeLibrary}
                  onChange={(e) => setExcludeLibrary(e.target.checked)}
                  className="accent-accent-primary"
                />
                Hide titles already in library
              </label>
            )}
          </div>
        </div>

        {/* Results region */}
        <div className="flex-1 overflow-y-auto">
          <TmdbSection
            query={trimmedQuery}
            showEmptyState={showEmptyState}
            recent={recent}
            onRecentClick={handleRecentClick}
            loading={tmdb.loading}
            error={tmdb.error}
            titles={tmdbTitles}
            people={tmdbPeople}
            libraryFilteredCount={libraryFilteredCount}
            inLibrary={inLibrary}
            hasMore={tmdb.hasMore}
            loadingMore={tmdb.loadingMore}
            onLoadMore={tmdb.loadMore}
            onRetry={tmdb.runNow}
            onPick={handlePickTmdb}
          />
        </div>
      </div>
    </div>
  )
}

// === TMDB section ===

function TmdbSection({
  query,
  showEmptyState,
  recent,
  onRecentClick,
  loading,
  error,
  titles,
  people,
  libraryFilteredCount,
  inLibrary,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
  onPick,
}: {
  query: string
  showEmptyState: boolean
  recent: string[]
  onRecentClick: (q: string) => void
  loading: boolean
  error: string | null
  titles: TmdbSearchResult[]
  people: TmdbSearchResult[]
  libraryFilteredCount: number
  inLibrary: { movies: Set<number>; series: Set<number> }
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  onRetry: () => void
  onPick: (r: TmdbSearchResult) => void
}) {
  if (showEmptyState) {
    return <EmptyState recent={recent} onRecentClick={onRecentClick} />
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />
  }

  if (loading && titles.length === 0 && people.length === 0) {
    return <SkeletonGrid />
  }

  if (!loading && titles.length === 0 && people.length === 0) {
    return <NoResultsState query={query} />
  }

  return (
    <div className="px-4 py-4">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        {titles.length + people.length} results
        {libraryFilteredCount > 0 && (
          <span className="ml-1 font-normal normal-case text-text-tertiary">
            · {libraryFilteredCount} hidden from library
          </span>
        )}
      </p>

      {titles.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {titles.map((r) => (
            <ResultTile
              key={`${r.kind}-${r.tmdbId}`}
              result={r}
              inLibrary={inLibrary}
              onPick={onPick}
            />
          ))}
          {hasMore && (
            <LoadMoreTile loadingMore={loadingMore} onClick={onLoadMore} />
          )}
        </div>
      )}

      {people.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
            People
          </p>
          <ul className="space-y-2">
            {people.map((r) => (
              <li key={`person-${r.tmdbId}`}>
                <button
                  type="button"
                  onClick={() => onPick(r)}
                  className="flex w-full items-center gap-3 rounded-md border border-border-primary bg-bg-secondary p-2 text-left hover:bg-bg-tertiary"
                >
                  <PersonThumb result={r} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">
                      {r.kind === 'person' ? r.name : ''}
                    </p>
                    <p className="truncate text-[11px] text-text-tertiary">
                      Person
                      {r.kind === 'person' && r.knownFor ? ` · ${r.knownFor}` : ''}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// === Result card ===

function ResultTile({
  result,
  inLibrary,
  onPick,
  whyItMatches,
  aiBadge = false,
}: {
  result: TmdbSearchResult
  inLibrary: { movies: Set<number>; series: Set<number> }
  onPick: (r: TmdbSearchResult) => void
  whyItMatches?: string
  aiBadge?: boolean
}) {
  if (result.kind === 'person') return null
  const poster = result.posterUrl
  const title = result.title
  const isInLibrary =
    result.kind === 'movie'
      ? inLibrary.movies.has(result.tmdbId)
      : inLibrary.series.has(result.tmdbId)

  return (
    <button
      type="button"
      onClick={() => onPick(result)}
      className="group flex flex-col text-left focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-primary"
      aria-label={`${title}${result.year ? ` (${result.year})` : ''}. ${
        result.kind === 'tv' ? 'TV series' : 'Movie'
      }. ${isInLibrary ? 'In your library. ' : ''}TMDB rating ${result.rating.toFixed(1)}.`}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]">
        {poster ? (
          <LazyImage
            src={poster}
            alt={title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
                {title}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
            {title}
          </div>
        )}

        {/* Rating badge (top-left) */}
        {result.rating > 0 && (
          <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-blue-500/80 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
            ★ {result.rating.toFixed(1)}
          </div>
        )}

        {/* In-library badge (top-right) */}
        {isInLibrary && (
          <div className="absolute right-1 top-1 flex items-center gap-0.5 rounded bg-accent-gold/80 px-1.5 py-0.5 text-[9px] font-semibold text-black backdrop-blur-sm">
            ★ In library
          </div>
        )}

        {/* Type badge (bottom-left) */}
        <div className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
          {aiBadge ? '✨ AI' : result.kind === 'tv' ? 'TV' : 'Movie'}
        </div>
      </div>

      <p className="mt-1.5 line-clamp-2 text-xs font-medium text-text-primary">{title}</p>
      {result.year != null && (
        <p className="text-[10px] font-medium text-accent-gold">{result.year}</p>
      )}
      {whyItMatches && (
        <p className="mt-1 line-clamp-2 text-[10px] text-text-secondary">{whyItMatches}</p>
      )}
    </button>
  )
}

function LoadMoreTile({
  loadingMore,
  onClick,
}: {
  loadingMore: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loadingMore}
      aria-label={loadingMore ? 'Loading more results' : 'Load more results'}
      className="group flex aspect-[2/3] w-full flex-col items-center justify-center rounded-lg border border-border-primary bg-bg-secondary p-4 text-center transition-colors hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:cursor-wait disabled:opacity-75"
    >
      {loadingMore ? (
        <>
          <Spinner />
          <span className="mt-3 text-xs font-semibold text-text-secondary">Loading…</span>
        </>
      ) : (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-primary/10 transition-colors group-hover:bg-accent-primary/20">
            <svg
              className="h-6 w-6 text-accent-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <span className="mt-3 text-xs font-semibold text-text-primary">Load more</span>
        </>
      )}
    </button>
  )
}

function PersonThumb({ result }: { result: TmdbSearchResult }) {
  if (result.kind !== 'person') return null
  if (!result.profileUrl) {
    return (
      <div className="aspect-square h-12 w-12 shrink-0 rounded-full border border-border-primary bg-bg-tertiary" />
    )
  }
  return (
    <LazyImage
      src={result.profileUrl}
      alt=""
      wrapperClassName="aspect-square h-12 w-12 shrink-0 rounded-full border border-border-primary"
      placeholderColor="#1a1a1a"
    />
  )
}

// === States ===

function EmptyState({
  recent,
  onRecentClick,
}: {
  recent: string[]
  onRecentClick: (q: string) => void
}) {
  return (
    <div className="px-4 py-10">
      <div className="mx-auto max-w-md text-center">
        <div className="mb-4 text-4xl opacity-60">🔍</div>
        <h3 className="mb-2 text-lg font-semibold text-text-primary">
          Search TMDB
        </h3>
        <p className="text-sm text-text-secondary">
          Start typing a movie or TV show title. Results appear as you type.
        </p>
        <p className="mt-3 text-xs text-text-tertiary">
          Tip: toggle to ✨ AI mode to describe what you want to watch in
          natural language.
        </p>
      </div>
      {recent.length > 0 && (
        <RecentList recent={recent} onRecentClick={onRecentClick} />
      )}
    </div>
  )
}

function NoResultsState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <div className="mb-3 text-4xl opacity-60">🔍</div>
      <h3 className="mb-2 text-lg font-semibold text-text-primary">
        No results
      </h3>
      <p className="max-w-sm text-sm text-text-secondary">
        No matches for <span className="font-semibold">&ldquo;{query}&rdquo;</span>.
        Try different keywords or switch to AI mode.
      </p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <div className="mb-3 text-4xl opacity-60">⚠️</div>
      <h3 className="mb-2 text-lg font-semibold text-text-primary">
        Search failed
      </h3>
      <p className="mb-4 max-w-sm text-sm text-accent-danger">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-accent-primary px-4 py-2 text-sm font-semibold text-white hover:bg-accent-primary/90"
      >
        Try again
      </button>
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div className="px-4 py-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="flex flex-col">
            <div className="aspect-[2/3] w-full animate-pulse rounded-lg border border-border-primary bg-bg-secondary" />
            <div className="mt-1.5 h-3 w-3/4 animate-pulse rounded bg-bg-secondary" />
            <div className="mt-1 h-2 w-1/3 animate-pulse rounded bg-bg-secondary" />
          </div>
        ))}
      </div>
    </div>
  )
}

function RecentList({
  recent,
  onRecentClick,
}: {
  recent: string[]
  onRecentClick: (q: string) => void
}) {
  return (
    <div className="mx-auto mt-8 max-w-md">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Recent searches
      </p>
      <div className="flex flex-wrap gap-2">
        {recent.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onRecentClick(q)}
            className="rounded-full border border-border-primary bg-bg-secondary px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  )
}

// === Icons ===

function SearchIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-accent-primary"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}
